/**
 * DataTable Organism
 *
 * Tabela de dados reutilizável com layout mobile-first
 * - Mobile: Cards empilhados
 * - Desktop: Tabela tradicional
 */
import React from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import Pagination from '../molecules/Pagination'
import { LoadingScreen } from '../atoms/Spinner'
import EmptyState from '../atoms/EmptyState'

export default function DataTable({
	columns,
	data,
	loading,
	error,
	// Pagination
	page,
	total,
	totalPages,
	limit,
	onPageChange,
	// Sorting
	sortBy,
	sortOrder,
	onSort,
	// Selection
	selectable = false,
	selectedIds = [],
	onSelectionChange,
	// Empty state
	emptyTitle = 'Nenhum item encontrado',
	emptyDescription,
	emptyMessage,
	emptyIcon,
	// Actions
	actions,
	// Other
	rowKey = '_id',
	onRowClick,
	className = '',
	// Mobile config
	mobileColumns = null, // Columns to show on mobile card (default: first 2)
}) {
	const handleSort = (column) => {
		if (!column.sortable || !onSort) return

		const newOrder = sortBy === column.key && sortOrder === 'asc' ? 'desc' : 'asc'
		onSort(column.key, newOrder)
	}

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			onSelectionChange?.(data.map((item) => item[rowKey]))
		} else {
			onSelectionChange?.([])
		}
	}

	const handleSelectRow = (id) => {
		if (selectedIds.includes(id)) {
			onSelectionChange?.(selectedIds.filter((i) => i !== id))
		} else {
			onSelectionChange?.([...selectedIds, id])
		}
	}

	const getSortIcon = (column) => {
		if (!column.sortable) return null
		if (sortBy !== column.key) return <ArrowUpDown className="w-4 h-4 text-text-body" />
		if (sortOrder === 'asc') return <ArrowUp className="w-4 h-4 text-primary" />
		return <ArrowDown className="w-4 h-4 text-primary" />
	}

	// Determine which columns to show on mobile
	const getMobileColumns = () => {
		if (mobileColumns) return columns.filter(c => mobileColumns.includes(c.key))
		// Default: first column (main) and status/badge columns
		return columns.slice(0, 1)
	}

	const getSecondaryMobileColumns = () => {
		if (mobileColumns) return columns.filter(c => !mobileColumns.includes(c.key))
		return columns.slice(1)
	}

	if (loading) {
		return <LoadingScreen />
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-12 text-danger">
				{error}
			</div>
		)
	}

	if (!data || data.length === 0) {
		return (
			<EmptyState
				icon={emptyIcon}
				title={emptyTitle || emptyMessage}
				description={emptyDescription}
			/>
		)
	}

	const primaryColumns = getMobileColumns()
	const secondaryColumns = getSecondaryMobileColumns()

	return (
		<div className={className}>
			{/* Mobile View - Cards */}
			<div className="block lg:hidden space-y-3">
				{data.map((item, idx) => (
					<div
						key={item[rowKey] || idx}
						className={`
							bg-base-300 rounded-lg border border-base-200 p-3
							${onRowClick ? 'cursor-pointer active:bg-base-400' : ''}
							${selectedIds.includes(item[rowKey]) ? 'border-primary bg-primary/5' : ''}
						`}
						onClick={() => onRowClick?.(item)}
					>
						{/* Header with checkbox and actions */}
						<div className="flex items-start justify-between gap-2 mb-2">
							<div className="flex items-start gap-2 flex-1 min-w-0">
								{selectable && (
									<input
										type="checkbox"
										checked={selectedIds.includes(item[rowKey])}
										onChange={() => handleSelectRow(item[rowKey])}
										onClick={(e) => e.stopPropagation()}
										className="mt-1 rounded border-base-200 text-primary focus:ring-primary"
									/>
								)}
								{/* Primary column content */}
								<div className="flex-1 min-w-0">
									{primaryColumns.map((column) => (
										<div key={column.key}>
											{column.render ? column.render(item, item) : item[column.key]}
										</div>
									))}
								</div>
							</div>
							{/* Actions */}
							{actions && (
								<div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
									{actions(item).map((action, actionIdx) => (
										<button
											key={actionIdx}
											onClick={action.onClick}
											className={`p-2 rounded-lg transition-colors ${
												action.variant === 'danger'
													? 'hover:bg-danger/20 text-danger'
													: 'hover:bg-base-400 text-text-body hover:text-text'
											}`}
											title={action.label}
										>
											{action.icon && <action.icon className="w-4 h-4" />}
										</button>
									))}
								</div>
							)}
						</div>

						{/* Secondary info - grid of badges/stats */}
						{secondaryColumns.length > 0 && (
							<div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t border-base-200 text-sm">
								{secondaryColumns.map((column) => (
									<div key={column.key} className="flex items-center gap-1">
										{column.render ? column.render(item, item) : (
											<>
												<span className="text-text-muted">{column.label}:</span>
												<span className="text-text">{item[column.key]}</span>
											</>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				))}
			</div>

			{/* Desktop View - Table */}
			<div className="hidden lg:block overflow-x-auto bg-base-300 rounded-lg border border-base-200">
				<table className="w-full">
					<thead>
						<tr className="border-b border-base-200">
							{selectable && (
								<th className="px-4 py-3 text-left">
									<input
										type="checkbox"
										checked={selectedIds.length === data.length}
										onChange={handleSelectAll}
										className="rounded border-base-200 text-primary focus:ring-primary"
									/>
								</th>
							)}
							{columns.map((column) => (
								<th
									key={column.key}
									className={`
										px-4 py-3 text-left text-xs font-medium text-text-body uppercase tracking-wider
										${column.sortable ? 'cursor-pointer hover:bg-base-400' : ''}
										${column.className || ''}
									`}
									onClick={() => handleSort(column)}
								>
									<div className="flex items-center gap-1">
										{column.label}
										{getSortIcon(column)}
									</div>
								</th>
							))}
							{actions && (
								<th className="px-4 py-3 text-right text-xs font-medium text-text-body uppercase tracking-wider">
									Ações
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{data.map((item, idx) => (
							<tr
								key={item[rowKey] || idx}
								className={`
									border-b border-base-200 last:border-b-0
									${onRowClick ? 'cursor-pointer hover:bg-base-400' : 'hover:bg-base-400/50'}
									${selectedIds.includes(item[rowKey]) ? 'bg-primary/10' : ''}
								`}
								onClick={() => onRowClick?.(item)}
							>
								{selectable && (
									<td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
										<input
											type="checkbox"
											checked={selectedIds.includes(item[rowKey])}
											onChange={() => handleSelectRow(item[rowKey])}
											className="rounded border-base-200 text-primary focus:ring-primary"
										/>
									</td>
								)}
								{columns.map((column) => (
									<td
										key={column.key}
										className={`px-4 py-4 text-sm text-text ${column.cellClassName || ''}`}
									>
										{column.render
											? column.render(item, item)
											: item[column.key]}
									</td>
								))}
								{actions && (
									<td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
										<div className="flex items-center justify-end gap-2">
											{actions(item).map((action, actionIdx) => (
												<button
													key={actionIdx}
													onClick={action.onClick}
													className={`p-1.5 rounded-lg transition-colors ${
														action.variant === 'danger'
															? 'hover:bg-danger/20 text-danger'
															: 'hover:bg-base-400 text-text-body hover:text-text'
													}`}
													title={action.label}
												>
													{action.icon && <action.icon className="w-4 h-4" />}
												</button>
											))}
										</div>
									</td>
								)}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{(totalPages > 1 || (total && limit && total > limit)) && (
				<div className="mt-4">
					<Pagination
						page={page}
						total={total}
						totalPages={totalPages}
						limit={limit}
						onPageChange={onPageChange}
					/>
				</div>
			)}
		</div>
	)
}
